// ─────────────────────────────────────────────
// Shared types – mirrors backend/src/types/index.ts
// (Timestamps become string or Date on the client)
// ─────────────────────────────────────────────

export interface User {
  uid: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  photoURL?: string;
  handicap?: number;
  courses: CourseSummary[];
  isLookingForGame?: boolean;
  preferences?: UserPreferences;
  createdAt?: string;
  lastLoginAt?: string;
}

export interface UserPreferences {
  defaultEventType?: EventType;
  favoriteEventTypes?: EventType[];
  favoriteGamePresets?: string[];
  favoriteCourses?: string[];
  defaultTeeBox?: string;
  favoriteGroups?: string[];
  recentlyPlayedWith?: string[];
  defaultTab?: string;
}

export interface CourseSummary {
  id: string;
  name: string;
  logoUrl?: string;
  isHome?: boolean;
}

export interface Friend {
  id: string;
  userId: string;
  friendId: string;
  status: "pending" | "accepted" | "declined";
  createdAt?: string;
  user?: User; // Populated by enriched API responses
}

export interface Group {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  createdBy: string;
  creatorName: string;
  memberIds: string[];
  adminIds: string[];
  pendingCount: number;
  members?: User[];
  createdAt?: string;
}

export enum EventType {
  CASUAL_ROUND = "casual-round",
  LEAGUE_GAME = "league-game",
  TOURNAMENT = "tournament",
  MATCH = "match",
  TRIP = "trip",
  OUTING = "outing",
  PARIMUTUEL_BETTING = "parimutuel-betting",
}

export enum EventStatus {
  DRAFT = "draft",
  SCHEDULED = "scheduled",
  IN_PROGRESS = "inProgress",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
}

export interface Event {
  id: string;
  name: string;
  eventType: EventType;
  createdBy: string;
  createdByName: string;
  playerIds: string[];
  pendingCount?: number;
  rsvpDeadline?: string;
  rsvpDeadlineText?: string;
  rounds: GameRound[];
  status: EventStatus;
  location?: string;
  notes?: string;
  chatId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GameRound {
  id: string;
  roundNumber: number;
  courseId: string;
  courseName: string;
  teeBox: string;
  date: string;
  scheduledTime?: string;
  formatSegments: FormatSegment[];
  status: "scheduled" | "inProgress" | "completed" | "cancelled";
}

export interface FormatSegment {
  id: string;
  format: string;
  holes: number[];
  gameConfig: GameConfig;
}

export interface GameConfig {
  templateId: string;
  teamMode: string;
  scoringMethod: string;
  handicapMethod?: string;
  stakes?: {
    type: "money" | "points" | "braggingRights";
    amount?: number;
    description?: string;
  };
}

// ─────────────────────────────────────────────
// Parimutuel Betting
// ─────────────────────────────────────────────

export interface PariMutuelParticipant {
  id: string;
  name: string; // e.g. "3 - Mystik Dan" for horses, "Tiger Woods" for golfers
  scratched?: boolean;
}

export interface PariMutuelReconciliation {
  winners: Array<{ bettorName: string; amount: number }>;
  totalUnawarded: number;
  reconciledAt?: string;
  reconciledBy: string;
}

export interface PariMutuelEvent {
  id: string;
  eventId: string;
  eventName: string;
  createdBy: string;
  status: "pending" | "open" | "locked" | "settled";
  shareCode?: string; // undefined for events created before this feature
  minBet?: number;
  maxBet?: number;
  showAllBets?: boolean;
  participants: PariMutuelParticipant[];
  bets: PariMutuelBet[];
  parameters?: PariMutuelParameters;
  payouts?: PariMutuelPayouts;
  reconciliation?: PariMutuelReconciliation;
  createdAt?: string;
}

export interface PariMutuelBet {
  id: string;
  bettorName: string;
  userId?: string;
  participantId: string;
  participantName: string;
  horseNumber: number;
  horseName?: string;
  winAmount: number;
  placeAmount: number;
  showAmount: number;
  paid?: boolean; // undefined/true = paid; false = pending payment
  voided?: boolean; // true = excluded from all pool calculations
  deviceId?: string;
  source?: "admin" | "guest";
  createdAt?: string;
}

export interface PariMutuelParameters {
  winParticipantId: string;
  placeParticipantId: string;
  showParticipantId: string;
  settledAt?: string;
  settledBy?: string;
}

export interface PariMutuelPayouts {
  totalPool: number;
  winPool: number;
  placePool: number;
  showPool: number;
  // Money that nobody won (no bets on the winning horse in that pool)
  unawardedWin: number;
  unawardedPlace: number;
  unawardedShow: number;
  payoutRows: PariMutuelPayoutRow[];
}

export interface PariMutuelPayoutRow {
  bettorName: string;
  userId?: string;
  participantId: string;
  participantName: string;
  horseNumber: number;
  horseName?: string;
  winBet: number;
  placeBet: number;
  showBet: number;
  winPayout: number;
  placePayout: number;
  showPayout: number;
  totalPayout: number;
  scratched?: boolean; // true = this is a scratch refund row
}

// ─────────────────────────────────────────────
// API
// ─────────────────────────────────────────────

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}
